import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { toast } from '../../toasts';
import { Button, FormField, Select, controlClass, textareaClass } from '../EntityList';
import documentService from '../../services/document.service';
import { getObjectServiceConfig } from '../../constants/objectServiceConfig';
import { getError } from '../../utils';

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

// Get document type label for display
const getDocumentTypeLabel = (docType) => {
	switch (docType) {
	case 'escritura':
		return 'Escritura (Activos)';
	case 'comprobante':
		return 'Comprobante';
	case 'document':
		return 'Documento (Ventas)';
	default:
		return getObjectServiceConfig(docType)?.label || docType || '';
	}
};

// Find the object linked to this document when editing
const findLinkedObjectId = (initialData, config, objects) => {
	if (!initialData?._id || !config || !objects.length) {
		return null;
	}
	const documentIdStr = initialData._id.toString();
	const linkedObject = objects.find((obj) => {
		if (obj.documents && Array.isArray(obj.documents)) {
			return obj.documents.some((docRef) => {
				const docId = docRef?._id || docRef;
				return docId && docId.toString() === documentIdStr;
			});
		}
		return false;
	});
	return linkedObject ? linkedObject._id : null;
};

const DocumentEditor = ({ show, onHide, onSuccess, initialData = null, documentType = null, objectType = null, objects = [], readonlyObject = false }) => {
	const config = getObjectServiceConfig(objectType);
	const isFullDocument = !!(initialData && initialData._id);

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [associatedObjectId, setAssociatedObjectId] = useState('');
	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});

	useEffect(() => {
		if (!show) return;
		setName(initialData?.name || '');
		setDescription(initialData?.description || '');
		const linkedObjectId = findLinkedObjectId(initialData, config, objects);
		setAssociatedObjectId(
			config
				? (isFullDocument ? (linkedObjectId || initialData?.[config.fieldName] || '') : (initialData?.[config.fieldName] || ''))
				: '',
		);
		// A file dropped/picked on the manager before the modal opened
		// (SingleObjectDocumentManager's dropzone) arrives pre-attached on
		// initialData.file — don't discard it under a plain edit's blank state.
		setFile(initialData?.file instanceof File ? initialData.file : null);
		setFieldErrors({});
	}, [show, initialData, config, objects, isFullDocument]);

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const objectOptions = objects.map((obj) => ({
		value: obj._id,
		label: obj[config?.displayField || 'name'] || obj.customId || obj._id,
	}));

	const getFieldErrors = () => {
		const errors = {};
		if (!isFullDocument && !file) errors.file = REQUIRED_FIELD_MESSAGE;
		if (!name.trim()) errors.name = REQUIRED_FIELD_MESSAGE;
		if (config && !associatedObjectId) errors.associatedObjectId = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (uploading) return;

		const errors = getFieldErrors();
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			return;
		}
		setFieldErrors({});

		setUploading(true);
		try {
			if (!documentType) {
				toast.error('Debe seleccionar un tipo de documento');
				setUploading(false);
				return;
			}

			if (isFullDocument) {
				// Update existing document
				const updatePayload = {
					name,
					description,
					documentType,
				};

				if (file instanceof File) {
					// If a new file is provided, upload it first
					const formDataUpload = new FormData();
					const r = Math.random().toString(36).substring(7);
					formDataUpload.append('file', file, (file.name || '').replaceAll(' ', '') + r);

					const uploadResponse = await documentService.upload(formDataUpload, documentType);
					updatePayload.fileLink = uploadResponse.data.fileLink;
					updatePayload.s3_key = uploadResponse.data.s3_key;
				}

				await documentService.edit(initialData._id, updatePayload);

				toast.success('Documento actualizado');
			} else {
				// Create new document
				const formDataUpload = new FormData();
				const r = Math.random().toString(36).substring(7);
				formDataUpload.append('file', file, (file.name || '').replaceAll(' ', '') + r);

				const uploadResponse = await documentService.upload(formDataUpload, documentType);

				const createPayload = {
					fileLink: uploadResponse.data.fileLink,
					s3_key: uploadResponse.data.s3_key,
					documentType,
					name,
					description,
				};

				await documentService.create(createPayload, associatedObjectId, config?.fieldName);

				toast.success('Documento creado');
			}

			if (onSuccess) {
				onSuccess();
			}
			onHide();
		} catch (error) {
			toast.error(getError(error));
		} finally {
			setUploading(false);
		}
	};

	return (
		<Modal
			show={show}
			onHide={onHide}
			size='lg'
			contentClassName='entity-list-page el-modal tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<Modal.Header closeButton>
				<Modal.Title>
					{isFullDocument ? 'Editar Documento' : 'Nuevo Documento'}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form id='document-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
					<FormField id='document-file' label='Archivo' required={!isFullDocument}>
						<Form.Control
							id='document-file'
							type='file'
							className={controlClass}
							accept='application/pdf,image/*,.doc,.docx'
							onChange={(event) => {
								setFile(event.target.files?.[0] || null);
								clearFieldError('file');
							}}
							disabled={uploading}
						/>
						{file && (
							// The native file input can't reflect a File set
							// programmatically (e.g. dropped on the manager before
							// this modal opened) — show the selected name explicitly
							// so it isn't invisible until submit.
							<p className='tw:mt-1 tw:mb-0 tw:text-[12.5px] tw:text-ink-2'>
								Archivo seleccionado: {file.name}
							</p>
						)}
						<FieldError message={fieldErrors.file} />
					</FormField>
					<FormField id='document-type' label='Tipo de Documento' required>
						<Form.Control id='document-type' className={controlClass} value={getDocumentTypeLabel(initialData?.documentType || documentType)} disabled />
					</FormField>
					{config && (
						<FormField id='document-associated-object' label={config.label} required>
							<Select
								id='document-associated-object'
								options={objectOptions}
								value={associatedObjectId}
								onChange={(event) => {
									setAssociatedObjectId(event.target.value);
									clearFieldError('associatedObjectId');
								}}
								placeholder={`Seleccione un ${config.label.toLowerCase()}`}
								disabled={uploading || isFullDocument || readonlyObject}
							/>
							<FieldError message={fieldErrors.associatedObjectId} />
						</FormField>
					)}
					<FormField id='document-name' label='Nombre' required>
						<Form.Control
							id='document-name'
							className={controlClass}
							value={name}
							placeholder='Ingrese el nombre del documento'
							onChange={(event) => {
								setName(event.target.value);
								clearFieldError('name');
							}}
							disabled={uploading}
						/>
						<FieldError message={fieldErrors.name} />
					</FormField>
					<FormField id='document-description' label='Descripción'>
						<Form.Control
							id='document-description'
							as='textarea'
							rows={3}
							className={textareaClass}
							value={description}
							placeholder='Ingrese una descripción del documento'
							onChange={(event) => setDescription(event.target.value)}
							disabled={uploading}
						/>
					</FormField>

					<div className='tw:flex tw:justify-end tw:gap-2'>
						<Button onClick={onHide} disabled={uploading}>
							Cancelar
						</Button>
						<Button variant='primary' type='submit' disabled={uploading}>
							{uploading ? 'Guardando...' : isFullDocument ? 'Guardar' : 'Agregar'}
						</Button>
					</div>
				</Form>
			</Modal.Body>
		</Modal>
	);
};

DocumentEditor.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	onSuccess: PropTypes.func,
	initialData: PropTypes.object,
	documentType: PropTypes.string,
	objectType: PropTypes.string,
	objects: PropTypes.array,
	readonlyObject: PropTypes.bool,
};

export default DocumentEditor;
